import React from 'react';
import { Link } from 'react-router-dom';
import { Sprout, TrendingUp, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const ProductionPlanCard = ({ plan, loading, selectedSeason }) => {
  // Función para calcular el avance de un item (igual que en PlanProduccion)
  const calculateItemProgress = (stock, planificado) => {
    if (planificado === 0) return 100;
    if (stock >= planificado) return 100;
    return Math.floor((stock / planificado) * 100);
  };

  // Calcular el porcentaje de avance ponderado si el estado es 'Proyectado'
  const calculateProgress = () => {
    if (!plan || !plan.detalles || plan.detalles.length === 0) {
      return 0;
    }

    let totalWeight = 0;
    let weightedSum = 0;
    
    plan.detalles.forEach((detalle) => {
      const stock = detalle.stock_actual ?? 0;
      const planificado = detalle.cantidad_planificada ?? 0;
      const progress = calculateItemProgress(stock, planificado);
      
      totalWeight += planificado;
      weightedSum += progress * planificado;
    });
    
    if (totalWeight === 0) return 0;
    return Math.floor(weightedSum / totalWeight);
  };

  const getStatusInfo = () => {
    if (!plan) {
      return {
        label: 'SIN DATOS',
        icon: AlertCircle,
        color: 'bg-gray-500',
        textColor: 'text-gray-700',
        bgColor: 'bg-gray-50',
      };
    }

    switch (plan.estado) {
      case 'Inicial':
        return {
          label: 'Inicial',
          icon: Clock,
          color: 'bg-blue-500',
          textColor: 'text-blue-700',
          bgColor: 'bg-blue-50',
        };
      case 'Proyectado':
        return {
          label: 'Proyectado',
          icon: TrendingUp,
          color: 'bg-orange-500',
          textColor: 'text-orange-700',
          bgColor: 'bg-orange-50',
        };
      case 'Finalizado':
        return {
          label: 'Finalizado',
          icon: CheckCircle,
          color: 'bg-green-500',
          textColor: 'text-green-700',
          bgColor: 'bg-green-50',
        };
      default:
        return {
          label: plan.estado,
          icon: AlertCircle,
          color: 'bg-gray-500',
          textColor: 'text-gray-700',
          bgColor: 'bg-gray-50',
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;
  const progress = plan?.estado === 'Proyectado' ? calculateProgress() : null;

  if (loading) {
    return (
      <div className="production-plan-card loading">
        <div className="stat-content">
          <div className="stat-info">
            <p className="stat-name">Plan de Cosecha</p>
            <p className="stat-value">Cargando...</p>
          </div>
          <div className="stat-icon bg-gray-400">
            <Sprout size={24} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link to="/plan-cosecha" className="production-plan-card">
      <div className="stat-content">
        <div className="stat-info">
          <p className="stat-name">Plan de Cosecha</p>
          <div className="status-container">
            <span className={`status-badge ${statusInfo.bgColor} ${statusInfo.textColor}`}>
              <StatusIcon size={16} />
              {statusInfo.label}
            </span>
          </div>
          {progress !== null && (
            <div className="progress-section">
              <div className="progress-header">
                <span className="progress-label">Avance</span>
                <span className="progress-value">{progress}%</span>
              </div>
              <div className="progress-bar-container">
                <div 
                  className="progress-bar-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
        <div className={`stat-icon ${statusInfo.color}`}>
          <Sprout size={24} />
        </div>
      </div>
      {plan && (
        <div className="stat-footer">
          <TrendingUp size={16} />
          <span>Ver detalles del plan</span>
        </div>
      )}

      <style jsx>{`
        .production-plan-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: var(--spacing-6);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--gray-200);
          text-decoration: none;
          color: inherit;
          transition: all 0.2s ease-in-out;
          display: block;
        }

        .production-plan-card.loading {
          pointer-events: none;
          opacity: 0.7;
        }

        @media (max-width: 768px) {
          .production-plan-card {
            padding: var(--spacing-3);
            border-radius: var(--radius-md);
          }
        }

        .production-plan-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        .stat-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        
        .production-plan-card:has(.stat-footer) .stat-content {
          margin-bottom: var(--spacing-4);
        }

        .stat-info {
          flex: 1;
        }

        .stat-name {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          margin: 0 0 var(--spacing-2) 0;
        }

        .status-container {
          margin-bottom: var(--spacing-3);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-2);
          padding: var(--spacing-2) var(--spacing-3);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          font-weight: 600;
        }

        .progress-section {
          margin-top: var(--spacing-3);
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-2);
        }

        .progress-label {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          font-weight: 500;
        }

        .progress-value {
          font-size: var(--font-size-lg);
          font-weight: 700;
          color: var(--gray-900);
        }

        .progress-bar-container {
          width: 100%;
          height: 8px;
          background-color: var(--gray-200);
          border-radius: var(--radius-full);
          overflow: hidden;
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--orange-500), var(--orange-600));
          border-radius: var(--radius-full);
          transition: width 0.3s ease-in-out;
        }

        @media (max-width: 768px) {
          .stat-name {
            font-size: 0.625rem;
            line-height: 1;
          }

          .status-badge {
            font-size: 0.625rem;
            padding: 0.25rem 0.5rem;
            gap: 0.25rem;
          }

          .status-badge svg {
            width: 12px;
            height: 12px;
          }

          .progress-label {
            font-size: 0.625rem;
          }

          .progress-value {
            font-size: 1rem;
          }

          .progress-bar-container {
            height: 6px;
          }
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .stat-icon {
            width: 32px;
            height: 32px;
          }

          .stat-icon svg {
            width: 16px;
            height: 16px;
          }
        }

        .stat-footer {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          color: var(--primary-blue);
          font-size: var(--font-size-sm);
          font-weight: 500;
          padding-top: var(--spacing-3);
          border-top: 1px solid var(--gray-100);
        }

        @media (max-width: 768px) {
          .stat-footer {
            font-size: 0.625rem;
            gap: var(--spacing-1);
          }

          .stat-footer svg {
            width: 12px;
            height: 12px;
          }

          .stat-footer span {
            display: none;
          }
        }
      `}</style>
    </Link>
  );
};

export default ProductionPlanCard;
